import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Button, Breadcrumb, message, Modal } from 'antd';
import { HomeOutlined, CheckOutlined } from '@ant-design/icons';
import api from '../services/auth';

const squareBtnStyle = `
  .square-btn {
    height: auto !important;
    min-height: 0 !important;
    line-height: 1.2 !important;
    padding: 12px 8px !important;
    aspect-ratio: 1.618 / 1 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    font-weight: 600 !important;
    border-radius: 16px !important;
    background: rgba(255,255,255,0.72) !important;
    backdrop-filter: blur(20px) saturate(180%) !important;
    -webkit-backdrop-filter: blur(20px) saturate(180%) !important;
    border: 1px solid rgba(0,0,0,0.06) !important;
    color: #1d1d1f !important;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04) !important;
    transition: all 0.2s ease !important;
  }
  .square-btn:hover {
    background: rgba(255,255,255,0.9) !important;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1) !important;
    transform: translateY(-1px);
    color: #1d1d1f !important;
  }
  .square-btn:active {
    transform: translateY(0) scale(0.97);
    box-shadow: 0 1px 3px rgba(0,0,0,0.06) !important;
  }
  .square-btn.root-btn {
    background: #007AFF !important;
    color: #fff !important;
    border: none !important;
    box-shadow: 0 2px 8px rgba(0,122,255,0.3) !important;
  }
  .square-btn.root-btn:hover {
    background: #0A84FF !important;
    color: #fff !important;
    box-shadow: 0 4px 16px rgba(0,122,255,0.4) !important;
  }
  .square-btn.numeric-btn {
    background: rgba(0,122,255,0.08) !important;
    border: 1px solid rgba(0,122,255,0.2) !important;
    color: #007AFF !important;
  }
  .square-btn.numeric-btn:hover {
    background: rgba(0,122,255,0.15) !important;
    color: #007AFF !important;
  }
`;

function parseNodeName(name) {
  const match = name.match(/^(\d+(?:\.\d+)?)([\s\S]*)$/);
  if (match) {
    return {
      value: parseFloat(match[1]),
      unit: match[2].trim() || null,
      isNumeric: true
    };
  }
  return { value: null, unit: null, isNumeric: false };
}

const RecordPage = ({ isMobile }) => {
  const [projects, setProjects] = useState([]);
  const [nodesMap, setNodesMap] = useState({});
  const [navPath, setNavPath] = useState([]);
  const [currentNodes, setCurrentNodes] = useState([]);
  const [numericValue, setNumericValue] = useState(null);
  const [numericUnit, setNumericUnit] = useState(null);
  const [inputModalVisible, setInputModalVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputUnit, setInputUnit] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await api.get('/projects');
      const filtered = res.data.filter(p => !p.is_template);
      setProjects(filtered);
      setCurrentNodes(filtered.map(p => ({
        id: p.id,
        name: p.name,
        children: [],
        isProject: true,
      })));
    } catch (err) {
      console.error('加载项目失败:', err);
    }
  };

  const loadNodes = async (projectId) => {
    if (nodesMap[projectId]) return nodesMap[projectId];
    try {
      const res = await api.get(`/projects/${projectId}/nodes`);
      setNodesMap(prev => ({ ...prev, [projectId]: res.data.tree }));
      return res.data.tree;
    } catch (err) {
      console.error('加载节点失败:', err);
      return [];
    }
  };

  const handleNodeClick = async (node) => {
    if (node.isProject) {
      const children = await loadNodes(node.id);
      setNavPath([{ ...node, children }]);
      setCurrentNodes(children);
      setNumericValue(null);
      setNumericUnit(null);
    } else if (node.node_type === 'numeric') {
      setInputUnit(node.name);
      setInputValue('');
      setInputModalVisible(true);
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 100);
    } else if (node.children && node.children.length > 0) {
      setNavPath(prev => [...prev, node]);
      setCurrentNodes(node.children);
      setNumericValue(null);
      setNumericUnit(null);
    } else {
      setNavPath(prev => [...prev, node]);
      setCurrentNodes([]);
      const parsed = parseNodeName(node.name);
      if (parsed.isNumeric) {
        setNumericValue(parsed.value);
        setNumericUnit(parsed.unit);
      } else {
        setNumericValue(null);
        setNumericUnit(null);
      }
    }
  };

  const handleInputConfirm = () => {
    const val = parseFloat(inputValue);
    if (isNaN(val) || inputValue === '') {
      message.warning('请输入数值');
      return;
    }
    setNumericValue(val);
    setNumericUnit(inputUnit);
    setInputModalVisible(false);
    setNavPath(prev => {
      const numericNode = { id: '__numeric__', name: inputUnit, node_type: 'numeric' };
      return [...prev, numericNode];
    });
    setCurrentNodes([]);
  };

  const handleBreadcrumbClick = (index) => {
    if (index === -1) {
      setNavPath([]);
      setCurrentNodes(projects.map(p => ({
        id: p.id,
        name: p.name,
        children: [],
        isProject: true,
      })));
      setNumericValue(null);
      setNumericUnit(null);
    } else {
      const newPath = navPath.slice(0, index + 1);
      const node = newPath[newPath.length - 1];
      setNavPath(newPath);
      setCurrentNodes(node.children || []);
      setNumericValue(null);
      setNumericUnit(null);
    }
  };

  const handleSubmit = async () => {
    if (navPath.length === 0) {
      message.warning('请先选择项目节点');
      return;
    }
    const projectId = navPath[0].id;
    const nodePath = navPath.filter(n => n.id !== '__numeric__').map(n => n.name).join('/');

    let recordValue = null;
    if (numericValue !== null) {
      recordValue = numericUnit ? `${numericValue} ${numericUnit}` : String(numericValue);
    }

    try {
      await api.post('/records', { projectId, nodePath, value: recordValue, numericValue, numericUnit, recordedAt: new Date().toISOString() });
      message.success('记录成功');
      setNavPath([]);
      setCurrentNodes(projects.map(p => ({
        id: p.id,
        name: p.name,
        children: [],
        isProject: true,
      })));
      setNumericValue(null);
      setNumericUnit(null);
    } catch (err) {
      message.error('记录失败: ' + (err.response?.data?.error || err.message));
    }
  };

  const selectedPathStr = navPath.filter(n => n.id !== '__numeric__').map(n => n.name).join('/');
  const canSubmit = navPath.length > 0 && (numericValue !== null || !navPath.some(n => n.node_type === 'numeric'));
  const hasChildren = currentNodes.length > 0;
  const isRoot = navPath.length === 0;

  const breadcrumbItems = [
    {
      key: 'root',
      title: <a href="#root" onClick={(e) => { e.preventDefault(); handleBreadcrumbClick(-1); }}><HomeOutlined /> 项目</a>,
    },
    ...navPath.map((node, index) => ({
      key: node.id + index,
      title: index < navPath.length - 1 ? (
        <a href={`#nav-${node.id}`} onClick={(e) => { e.preventDefault(); handleBreadcrumbClick(index); }}>{node.name}</a>
      ) : (
        <span>{node.name}</span>
      ),
    }))
  ];

  const lastNode = navPath.length > 0 ? navPath[navPath.length - 1] : null;
  const lastNodeParsed = lastNode ? parseNodeName(lastNode.name) : { isNumeric: false };
  const isNumericNode = lastNode?.node_type === 'numeric';

  return (
    <div>
      <style>{squareBtnStyle}</style>
      <Breadcrumb style={{ marginBottom: 16 }} items={breadcrumbItems} />

      {selectedPathStr && (
        <div style={{
          padding: '10px 16px',
          background: 'rgba(52,199,89,0.08)',
          border: '1px solid rgba(52,199,89,0.2)',
          borderRadius: 12,
          marginBottom: 16,
          fontSize: isMobile ? 13 : 14,
          color: '#248a3d',
          fontWeight: 500,
        }}>
          当前选择：{selectedPathStr}
        </div>
      )}

      {!hasChildren && navPath.length > 0 && lastNodeParsed.isNumeric && !isNumericNode && (
        <div style={{
          marginBottom: 16,
          padding: 16,
          background: 'rgba(255,255,255,0.72)',
          backdropFilter: 'blur(20px) saturate(180%)',
          borderRadius: 12,
          border: '1px solid rgba(0,0,0,0.06)',
        }}>
          <div style={{ fontSize: 14, color: '#86868b', marginBottom: 8, fontWeight: 500 }}>
            识别到节点数值：{lastNodeParsed.value} {lastNodeParsed.unit}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              value={numericValue ?? ''}
              onChange={e => setNumericValue(e.target.value ? parseFloat(e.target.value) : null)}
              placeholder="输入数值"
              style={{
                flex: 1,
                height: 44,
                borderRadius: 12,
                border: '1px solid rgba(0,0,0,0.1)',
                padding: '0 14px',
                fontSize: 16,
                fontWeight: 500,
                outline: 'none',
                background: '#fff',
              }}
            />
            {numericUnit && (
              <span style={{ fontSize: 16, fontWeight: 600, color: '#007AFF', whiteSpace: 'nowrap' }}>{numericUnit}</span>
            )}
          </div>
        </div>
      )}

      {!hasChildren && isNumericNode && numericValue !== null && (
        <div style={{
          marginBottom: 16,
          padding: 20,
          background: 'rgba(0,122,255,0.06)',
          borderRadius: 16,
          border: '1px solid rgba(0,122,255,0.15)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#1d1d1f', letterSpacing: -1 }}>
            {numericValue}
            <span style={{ fontSize: 18, fontWeight: 500, color: '#86868b', marginLeft: 6 }}>{numericUnit}</span>
          </div>
          <Button
            type="link"
            style={{ color: '#007AFF', fontWeight: 500, marginTop: 4, padding: 0 }}
            onClick={() => {
              setInputValue(numericValue != null ? String(numericValue) : '');
              setInputUnit(numericUnit || '');
              setInputModalVisible(true);
              setTimeout(() => {
                if (inputRef.current) inputRef.current.focus();
              }, 100);
            }}
          >
            点击修改数值
          </Button>
        </div>
      )}

      {hasChildren ? (
        <Row gutter={[isMobile ? 10 : 12, isMobile ? 10 : 12]}>
          {currentNodes.map((node) => {
            const isNumeric = node.node_type === 'numeric';
            return (
              <Col xs={12} sm={8} md={6} lg={4} key={node.id}>
                <Button
                  size="large"
                  block
                  className={`square-btn${isRoot ? ' root-btn' : ''}${isNumeric ? ' numeric-btn' : ''}`}
                  onClick={() => handleNodeClick(node)}
                  style={{
                    fontSize: isMobile ? 36 : 28,
                  }}
                >
                  {isNumeric ? (
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.7 }}>自定义数值</span>
                      <span>{node.name}</span>
                    </span>
                  ) : node.name}
                </Button>
              </Col>
            );
          })}
        </Row>
      ) : (
        navPath.length > 0 && !isNumericNode && (
          <div style={{ textAlign: 'center', color: '#86868b', padding: 24, fontSize: 15, fontWeight: 500 }}>
            {lastNodeParsed.isNumeric ? '请确认数值后点击下方提交按钮记录' : '已到达末级节点，请点击下方提交按钮记录'}
          </div>
        )
      )}

      {canSubmit && (
        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            size="large"
            onClick={handleSubmit}
            style={{
              minWidth: isMobile ? '80%' : 200,
              height: 50,
              fontSize: 16,
              fontWeight: 600,
              borderRadius: 14,
              background: '#34C759',
              borderColor: '#34C759',
              boxShadow: '0 2px 8px rgba(52,199,89,0.3)',
            }}
          >
            提交记录
          </Button>
        </div>
      )}

      <Modal
        title={<span>输入数值{inputUnit ? `（${inputUnit}）` : ''}</span>}
        open={inputModalVisible}
        onCancel={() => setInputModalVisible(false)}
        onOk={handleInputConfirm}
        okText="确认"
        cancelText="取消"
        width={isMobile ? '90%' : 360}
        styles={{ body: { paddingTop: 16 } }}
        destroyOnClose
      >
        <input
          ref={inputRef}
          type="number"
          inputMode="decimal"
          step="0.01"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleInputConfirm(); }}
          placeholder={`请输入数值${inputUnit ? `，单位：${inputUnit}` : ''}`}
          style={{
            width: '100%',
            height: 52,
            borderRadius: 14,
            border: '2px solid rgba(0,122,255,0.2)',
            padding: '0 16px',
            fontSize: 24,
            fontWeight: 600,
            outline: 'none',
            background: '#fff',
            color: '#1d1d1f',
            textAlign: 'center',
            letterSpacing: 2,
          }}
        />
        {inputUnit && (
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: 15, color: '#86868b', fontWeight: 500 }}>
            单位：{inputUnit}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RecordPage;
